import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend, isUnauthorized } from './api'

/** Die Rechte, die der Server kennt (server/internal/auth/permissions.go). */
export type Permission =
  | 'view'
  | 'alert.ack'
  | 'remote.ssh'
  | 'remote.gui'
  | 'remote.tunnel'
  | 'live.start'
  | 'config.view'
  | 'config.restore'
  | 'device.manage'
  | 'agent.manage'
  | 'credential.manage'
  | 'credential.reveal'
  | 'user.manage'
  | 'settings.manage'
  | 'audit.view'

export interface Me {
  user_id: string
  email: string
  name: string
  global_permissions: Permission[]
  customer_permissions: Record<string, Permission[]>
  mfa_enabled: boolean
  /** Gesetzt für ein Konto des Kundenportals (Stufe 6.9). */
  portal_customer_id?: string
}

interface AuthValue {
  me: Me | null
  loading: boolean
  /** Hat der Benutzer das Recht bei mindestens einem Kunden? */
  can: (p: Permission) => boolean
  /** Hat der Benutzer das Recht bei diesem Kunden? */
  canForCustomer: (customerId: string, p: Permission) => boolean
  logout: () => void
  refresh: () => void
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const { data, isPending } = useQuery({
    queryKey: ['me'],
    queryFn: async ({ signal }) => {
      try {
        return await apiGet<Me>('/me', signal)
      } catch (error) {
        // Nicht angemeldet ist kein Fehler, sondern ein Zustand.
        if (isUnauthorized(error)) return null
        throw error
      }
    },
    retry: false,
    staleTime: 60_000,
  })

  const logout = useMutation({
    mutationFn: () => apiSend<void>('POST', '/logout'),
    onSettled: () => queryClient.clear(),
  })

  const me = data ?? null

  const can = useCallback(
    (p: Permission) => {
      if (!me) return false
      if (me.global_permissions.includes(p)) return true
      return Object.values(me.customer_permissions).some((perms) => perms.includes(p))
    },
    [me],
  )

  const canForCustomer = useCallback(
    (customerId: string, p: Permission) => {
      if (!me) return false
      if (me.global_permissions.includes(p)) return true
      return (me.customer_permissions[customerId] ?? []).includes(p)
    },
    [me],
  )

  const value = useMemo<AuthValue>(
    () => ({
      me,
      loading: isPending,
      can,
      canForCustomer,
      logout: () => logout.mutate(),
      refresh: () => void queryClient.invalidateQueries({ queryKey: ['me'] }),
    }),
    [me, isPending, can, canForCustomer, logout, queryClient],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden')
  return value
}
